import React from 'react';
import DodTermText from '../DodTermText';
import type { WillsTrustsAnswers } from '../constants/complianceChecklists';
import { US_FORMATION_STATES } from '../constants/businessCreationInterview';
import {
  nextWillsQuestion,
  retreatWillsAnswers,
  willsQuestionNumber,
  willsTrustsAnswerLines,
  type WillsLanguage,
  type WillsQuestionId,
} from '../constants/willsTrustsInterview';

type InterviewProps = {
  answers: WillsTrustsAnswers;
  language: WillsLanguage;
  onAnswer: (id: WillsQuestionId, value: string) => void;
  onBack: () => void;
  onStartOver: () => void;
};

const WillsTrustsInterview: React.FC<InterviewProps> = ({
  answers,
  language,
  onAnswer,
  onBack,
  onStartOver,
}) => {
  const question = nextWillsQuestion(answers, language);
  const spanish = language === 'es';
  const number = willsQuestionNumber(answers);
  const [draft, setDraft] = React.useState('');
  const canGoBack = number > 1;

  React.useEffect(() => {
    if (!question) {
      setDraft('');
      return;
    }
    const current = answers[question.id];
    setDraft(typeof current === 'string' ? current : '');
  }, [answers, question?.id]);

  if (!question) {
    return null;
  }

  const submitDraft = (event: React.FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) {
      return;
    }
    onAnswer(question.id, value);
  };

  return (
    <form className="business-creation-interview" onSubmit={submitDraft} aria-labelledby="wills-trusts-prompt">
      <p className="business-creation-progress">
        {spanish ? `Pregunta ${number}` : `Question ${number}`}
        {' · '}
        {spanish
          ? 'Esto no redacta un testamento ni lo presenta en un tribunal.'
          : 'This does not write a will or file it with a court.'}
      </p>
      <h3 id="wills-trusts-prompt">{question.prompt}</h3>
      <p className="business-creation-help">
        <DodTermText text={question.help} language={language} />
      </p>
      {question.kind === 'choice' && question.options ? (
        <ul className="business-creation-options">
          {question.options.map((option) => (
            <li key={option.value} className="business-creation-option-row">
              <button
                type="button"
                className="business-creation-option"
                onClick={() => onAnswer(question.id, option.value)}
              >
                <span className="business-creation-option-label">{option.label}</span>
              </button>
              <p className="business-creation-option-detail">
                <DodTermText text={option.hint} language={language} />
              </p>
            </li>
          ))}
        </ul>
      ) : null}
      {question.kind === 'state' ? (
        <label className="business-creation-field" htmlFor="wills-trusts-state">
          {spanish ? 'Estado' : 'State'}
          <select
            id="wills-trusts-state"
            value={draft}
            required
            onChange={(event) => onAnswer(question.id, event.target.value)}
          >
            <option value="">{spanish ? 'Elija un estado' : 'Choose a state'}</option>
            {US_FORMATION_STATES.map(([code, name]) => (
              <option key={code} value={code}>{name}</option>
            ))}
          </select>
        </label>
      ) : null}
      {question.kind === 'text' ? (
        <label className="business-creation-field" htmlFor="wills-trusts-person">
          {spanish ? 'Nombre legal' : 'Legal name'}
          <input
            id="wills-trusts-person"
            value={draft}
            placeholder={question.placeholder}
            onChange={(event) => setDraft(event.target.value)}
          />
        </label>
      ) : null}
      <div className="business-creation-nav">
        {canGoBack ? (
          <button type="button" className="dashboard-security-inline-btn" onClick={onBack}>
            {spanish ? 'Pregunta anterior' : 'Previous question'}
          </button>
        ) : null}
        {question.kind === 'text' ? (
          <button type="submit" className="dashboard-security-inline-btn">
            {spanish ? 'Guardar y seguir' : 'Save and continue'}
          </button>
        ) : null}
        <button type="button" className="dashboard-security-inline-btn" onClick={onStartOver}>
          {spanish ? 'Empezar de nuevo' : 'Start over'}
        </button>
      </div>
    </form>
  );
};

export const WillsTrustsSummary: React.FC<{
  answers: WillsTrustsAnswers;
  language: WillsLanguage;
  onRevise: () => void;
}> = ({ answers, language, onRevise }) => {
  const spanish = language === 'es';
  const lines = willsTrustsAnswerLines(answers, language);
  return (
    <section className="business-creation-summary" aria-label={spanish ? 'Sus respuestas' : 'Your answers'}>
      <div className="business-creation-summary-head">
        <h3>{spanish ? 'Sus respuestas' : 'Your answers'}</h3>
        <button type="button" className="dashboard-security-inline-btn" onClick={onRevise}>
          {spanish ? 'Cambiar la última respuesta' : 'Change the last answer'}
        </button>
      </div>
      <ul>
        {lines.map((line) => (
          <li key={line}>
            <DodTermText text={line} language={language} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export const willsBackFrom = (answers: WillsTrustsAnswers): WillsTrustsAnswers => retreatWillsAnswers(answers);

export default WillsTrustsInterview;
